import styled, { keyframes } from "styled-components";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { themePalette } from "../../theme";
import { TRUNCATION_CHAR_LIMIT } from "./constants";

const pulse = keyframes`
  0% { color: ${themePalette.greyDark}; }
  50% { color: ${themePalette.greyLight}; }
  100% { color: ${themePalette.greyDark}; }
`;

const getMessageIconColor = ({
  $isAgent,
  isUser,
}: {
  $isAgent?: boolean;
  isUser: boolean;
}) => {
  if ($isAgent) return themePalette.purpleDark;
  if (isUser) return themePalette.secondaryBackgroundColor;
  return themePalette.purpleMedium;
};

export const PulsingIcon = styled.div`
  display: inline-block;
  font-size: 1.25em;
  transform: translateY(4px);
  animation: ${pulse} 1.5s ease-in-out infinite;
  line-height: 1;
`;

export const ChatbotWidgetContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

export const ChatButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${themePalette.purpleMedium};
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 30px;
    height: 30px;
    color: ${themePalette.white};
  }
`;

export const ChatPopup = styled.div<{
  deviceType: DeviceType;
  isMinimized?: boolean;
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ deviceType }) => (deviceType === "desktop" ? "430px" : "100%")};
  min-height: ${({ isMinimized }) => (isMinimized ? "auto" : "300px")};
  height: ${({ isMinimized }) => (isMinimized ? "auto" : "auto")};
  max-height: ${({ isMinimized, deviceType }) => {
    if (isMinimized) return "auto";
    // Height on desktop should stop just below the header
    return deviceType === "desktop" ? "calc(100vh - 129px)" : "80vh";
  }};
  background: ${themePalette.purpleWhite};
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${themePalette.neutral200};
  box-sizing: border-box;

  ${({ deviceType }) =>
    deviceType !== "desktop" &&
    `
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
  `}
`;

export const ChatHeader = styled.div`
  background: ${themePalette.purpleWhite};
  color: ${themePalette.greyDark};
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  border-bottom: 1px solid ${themePalette.neutral200};
  cursor: pointer;

  > div {
    display: flex;
    align-items: center;
    gap: 18px;
  }
`;

export const ChatHeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 22px;
  font-weight: 700;
  color: ${themePalette.purpleMedium};
  text-shadow: 0.5px 0 0 ${themePalette.purpleMedium};

  svg {
    width: 30px;
    height: 30px;
    color: ${themePalette.purpleMedium};
  }
`;

export const HeaderButton = styled.button`
  background: none;
  border: none;
  color: ${themePalette.greyMedium};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${themePalette.neutral100};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 10px 20px 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: ${themePalette.purpleWhite};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${themePalette.neutral100};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${themePalette.greyLight};
    border-radius: 3px;
  }
`;

export const MessageWrapper = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${({ isUser }) => (isUser ? "flex-end" : "flex-start")};
  align-items: flex-start;
  gap: 8px;
`;

export const MessageIcon = styled.div<{
  isUser: boolean;
  $isAgent?: boolean;
}>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  order: ${({ isUser }) => (isUser ? 2 : 0)};
  align-self: flex-end;
  margin-bottom: 18px;

  svg {
    width: 25px;
    height: 25px;
    color: ${getMessageIconColor};
  }
`;

export const MessageContent = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 80%;
  position: relative;
  padding-bottom: 40px;
  align-items: ${({ isUser }) => (isUser ? "flex-end" : "flex-start")};
`;

export const Message = styled.div<{
  isUser: boolean;
  isQueued?: boolean;
  $isAgent?: boolean;
}>`
  position: relative;
  padding: 10px 16px;
  border-radius: 18px;
  background-color: ${({ $isAgent, isUser }) => {
    if ($isAgent) return themePalette.purpleDark;
    return isUser ? themePalette.blueLight : themePalette.purpleLight;
  }};
  color: ${({ $isAgent, isUser }) => {
    if ($isAgent) return themePalette.white;
    return isUser ? themePalette.greyDark : themePalette.purpleDark;
  }};
  font-size: 14px;
  line-height: 1.4;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  width: fit-content;
  min-width: 0;
  opacity: ${({ isQueued }) => (isQueued ? 0.7 : 1)};

  ${({ $isAgent, isUser }) => {
    if (isUser) return "border-bottom-right-radius: 4px;";
    if ($isAgent) return "border-bottom-left-radius: 4px;";
    return `border-bottom-left-radius: 4px; border: 1px solid ${themePalette.neutral200};`;
  }}

  p {
    margin: 0;
  }

  ul {
    margin-left: 0;
    margin-top: 4px;
    padding-left: 24px;
  }

  a {
    color: ${({ $isAgent }) =>
      $isAgent ? themePalette.blueMedium : themePalette.blueDarker};
    text-decoration: underline;

    &:active {
      color: ${themePalette.cyanBright};
    }

    &:visited {
      color: ${({ $isAgent }) =>
        $isAgent ? themePalette.greyLight : themePalette.greyMedium};
    }
  }
`;

export const AgentStatusMessage = styled.div<{
  $isError?: boolean;
  $isItalic?: boolean;
}>`
  color: ${({ $isError }) =>
    $isError ? themePalette.redMedium : themePalette.greyMedium};
  font-size: 13px;
  font-style: ${({ $isItalic }) => ($isItalic ? "italic" : "normal")};
  line-height: 1.4;
  margin: -4px auto 0 auto;
  max-width: 80%;
  text-align: center;
  white-space: pre-line;
`;

export const MessageDeliveryStatus = styled.div<{ isUser: boolean }>`
  color: ${themePalette.redMedium};
  font-size: 11px;
  margin-top: 10px;
  text-align: ${({ isUser }) => (isUser ? "right" : "left")};
  position: absolute;
  ${({ isUser }) => (isUser ? "right: 0;" : "left: 0;")}
  bottom: 16px;
  white-space: nowrap;
`;

export const TypingMessage = styled(Message)`
  padding: 5px 16px;
  width: fit-content;
  min-width: 0;
`;

export const TruncatedMessage = styled(Message)`
  position: relative;
  max-height: ${Math.ceil(TRUNCATION_CHAR_LIMIT / 50) * 20 + 40}px;
  overflow: hidden;
  padding-bottom: 30px;
  width: fit-content;
  min-width: 0;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(
      transparent 0%,
      ${themePalette.purpleLight} 50%,
      ${themePalette.purpleLight} 100%
    );
    pointer-events: none;
  }
`;

export const ExpandedMessage = styled(Message)`
  padding-bottom: 30px;
  width: fit-content;
  min-width: 0;
`;

export const WarningMessage = styled.div`
  background-color: ${themePalette.blueDarker};
  color: ${themePalette.white};
  padding: 16px 16px 16px 21px;
  font-size: 14px;
  line-height: 1.4;
  border-radius: 0;
  margin: -20px -10px 0 -10px;
`;

export const LanguageSupportMessage = styled.div`
  background-color: ${themePalette.purpleMediumDark};
  color: ${themePalette.white};
  padding: 6px 16px 6px 21px;
  font-size: 14px;
  line-height: 1.4;
  border-radius: 0;
  margin: -16px -10px 0 -10px;
`;

export const AgentConversationBanner = styled.div`
  min-height: 35px;
  background-color: ${themePalette.purpleMedium};
  color: ${themePalette.white};
  padding: 12px 16px 12px 21px;
  font-size: 14px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
`;

export const AgentConversationBannerText = styled.div`
  font-weight: 400;
`;

export const AgentDisconnectButton = styled.button`
  min-width: 0;
  height: 30px;
  border-radius: 20px;
  background: ${themePalette.purpleMedium};
  border: 2px solid ${themePalette.white};
  color: ${themePalette.white};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 0 6px;
  text-transform: uppercase;
  transition:
    transform 0.2s ease,
    background 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    background: ${themePalette.white};
    color: ${themePalette.purpleMediumDark};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

export const ShowMoreButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 12px;
  background: none;
  border: none;
  color: ${themePalette.purpleDark};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 1;
`;

export const MessageSender = styled.div<{ isUser: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ isUser }) =>
    isUser ? themePalette.blueDark : themePalette.purpleMedium};
  margin-bottom: 4px;
  text-align: ${({ isUser }) => (isUser ? "right" : "left")};
`;

export const MessageTime = styled.div<{ isUser: boolean }>`
  font-size: 11px;
  color: ${themePalette.greyMedium};
  margin-top: 10px;
  text-align: ${({ isUser }) => (isUser ? "right" : "left")};
  position: absolute;
  ${({ isUser }) => (isUser ? "right: 0;" : "left: 0;")}
  bottom: 16px;
  white-space: nowrap;
`;

export const ChatForm = styled.form`
  padding: 16px 20px;
  background: ${themePalette.purpleWhite};
  border-top: 1px solid ${themePalette.neutral200};
  position: relative;
`;

export const ChatInputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: ${themePalette.purpleDark};
  margin-bottom: 8px;
`;

export const ChatInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

export const ChatInput = styled.textarea`
  width: 100%;
  padding: 8px 44px 8px 16px;
  border: 1px solid ${themePalette.neutral200};
  border-radius: 19px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease;
  height: 38px;
  min-height: 38px;
  max-height: 120px;
  box-sizing: border-box;
  resize: none;
  overflow-y: auto;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;

  &:focus {
    border-color: ${themePalette.purpleMedium};
  }

  &::placeholder {
    color: ${themePalette.greyLight};
  }

  &:disabled {
    background-color: ${themePalette.neutral100};
    cursor: default;
  }

  /* Hide scrollbar for all browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SendButton = styled.button`
  position: absolute;
  right: 5px;
  bottom: 50%;
  transform: translateY(50%);
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: ${themePalette.purpleDark};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active:not(:disabled) {
    transform: translateY(50%) scale(0.95);
  }

  &:disabled {
    background: ${themePalette.purpleMedium};
    cursor: default;
    opacity: 0.6;
  }

  svg {
    width: 17px;
    height: 17px;
    color: ${themePalette.white};
    margin-left: 1px;
  }
`;

export const FeedbackButtons = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
`;

export const FeedbackButton = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 17.5px;
  background: ${themePalette.white};
  border: none;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    background 0.2s ease;
  line-height: 1;
  color: ${themePalette.purpleMediumDark};

  svg {
    transition: color 0.2s ease;
  }

  &:hover:not(:disabled) {
    transform: scale(1.1);
    background: ${themePalette.purpleMediumDark};
    color: ${themePalette.white};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: default;
  }

  &.selected {
    background: ${themePalette.purpleMediumDark};
    color: ${themePalette.white};
  }
`;

export const AgentConnectButton = styled.button`
  min-width: 92px;
  height: 35px;
  border-radius: 17.5px;
  background: ${themePalette.purpleMediumDark};
  border: none;
  color: ${themePalette.white};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  text-transform: uppercase;
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    background: ${themePalette.white};
    color: ${themePalette.purpleMediumDark};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

export const AgentDeclineButton = styled(AgentConnectButton)`
  min-width: 0;
  background: ${themePalette.white};
  border: none;
  color: ${themePalette.purpleMediumDark};

  &:hover:not(:disabled) {
    background: ${themePalette.purpleMediumDark};
    color: ${themePalette.white};
  }
`;

export const FeedbackPillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

export const FeedbackPill = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  background: ${themePalette.white};
  border: 2px solid ${themePalette.purpleMediumDark};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${themePalette.purpleMediumDark};

  &:hover:not(:disabled) {
    transform: scale(1.02);
    background: ${themePalette.neutral100};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: default;
  }

  &:disabled:not(.selected) {
    opacity: 0.5;
  }

  &.selected {
    background: ${themePalette.purpleMediumDark};
    color: ${themePalette.white};
  }
`;
